import { useCallback, useRef, useState } from "react";

import { useSalesRequestRealtimeRefresh } from "~/hooks/useSalesRequestRealtimeRefresh";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

const POR_FACTURAR_INITIAL_SIZE = 25;
const POR_FACTURAR_LOAD_MORE_SIZE = 25;

// Shared row mappers, used by both the paged fetch and the realtime refresh.
const extractClientNames = (rows: any[]): Record<string, string> => {
  const names: Record<string, string> = {};
  rows.forEach((req) => {
    if (req.clients && req.clients.name) {
      names[req.clients.id] = req.clients.name;
    }
  });
  return names;
};

const mapRow = (req: any): SalesRequest => ({
  id: req.id,
  clientId: req.client_id,
  createdBy: req.created_by,
  approvedRejectedBy: req.approved_rejected_by || undefined,
  status: req.status as "por_facturar",
  paid: req.paid,
  paid_amount: req.paid_amount || 0,
  payment_count: 0,
  paymentTerms: req.payment_terms,
  paymentType: req.payment_type as "future" | "prepaid",
  paymentDueDate: req.payment_due_date,
  totalAmount: req.total_amount,
  price_with_iva: req.price_with_iva,
  hasPricingWarning: req.has_pricing_warning || false,
  comments: req.comments || undefined,
  internal_notes: req.internal_notes || undefined,
  factura_observations: req.factura_observations || undefined,
  deliveryDate: req.delivery_date || undefined,
  deliveryAddressId: req.delivery_address_id || undefined,
  deliveryStreet: req.delivery_street || undefined,
  deliveryNumber: req.delivery_number || undefined,
  deliveryComplement: req.delivery_complement || undefined,
  deliveryCity: req.delivery_city || undefined,
  deliveryState: req.delivery_state || undefined,
  deliveryComuna: req.delivery_comuna || undefined,
  rejectedReason: req.rejected_reason || undefined,
  createdAt: req.created_at,
  updatedAt: req.updated_at,
  salesConditionId: req.sales_condition_id || undefined,
  assigned_transportista: req.assigned_transportista || undefined,
  factura_number: undefined,
});

export const usePorFacturarSalesNotes = (viewAllSellers: boolean = false) => {
  const [porFacturarNotes, setPorFacturarNotes] = useState<SalesRequest[]>([]);
  const [porFacturarClientNames, setPorFacturarClientNames] = useState<
    Record<string, string>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // True once the first fetch settles (success or error), so consumers can
  // distinguish "not started yet" from "finished" (isLoading starts false).
  const [hasLoaded, setHasLoaded] = useState(false);
  const pageRef = useRef(0);
  const isInitialized = useRef(false);

  const { id: currentUserId, role: userRole } = useProfileStore();

  const fetchPorFacturarNotes = useCallback(
    async (loadMore: boolean = false) => {
      // Prevent multiple simultaneous calls for initial load
      if (!loadMore && (isLoading || isInitialized.current)) {
        return;
      }

      if (loadMore) {
        if (isLoadingMore) return; // Prevent multiple load more calls
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        pageRef.current = 0;
        setHasMore(true);
        isInitialized.current = true;
      }

      try {
        const currentPage = loadMore ? pageRef.current : 0;
        const pageSize = loadMore
          ? POR_FACTURAR_LOAD_MORE_SIZE
          : POR_FACTURAR_INITIAL_SIZE;

        const actualOffset = loadMore
          ? POR_FACTURAR_INITIAL_SIZE +
            currentPage * POR_FACTURAR_LOAD_MORE_SIZE
          : 0;

        let query = supabase
          .from("sales_request")
          .select(
            `
          *,
          paid_amount,
          clients:client_id(id, name, risk_checked),
          profiles:created_by(id, name, lastname, email, role)
        `,
            { count: "exact" }
          )
          .eq("status", "por_facturar")
          .is("deleted_at", null)
          .range(actualOffset, actualOffset + pageSize - 1);

        // Apply role-based filtering
        if (userRole === "seller" && currentUserId && !viewAllSellers) {
          query = query.eq("created_by", currentUserId);
        } else if (userRole === "transportista") {
          query = query.eq("assigned_transportista", currentUserId);
        }

        const { data, error, count } = await query.order("created_at", {
          ascending: false,
        });

        if (error) throw error;

        if (data) {
          const newClientNames = extractClientNames(data);
          const formattedRequests = data.map(mapRow);

          if (loadMore) {
            setPorFacturarNotes((prev) => [...prev, ...formattedRequests]);
            setPorFacturarClientNames((prev) => ({
              ...prev,
              ...newClientNames,
            }));
          } else {
            setPorFacturarNotes(formattedRequests);
            setPorFacturarClientNames(newClientNames);
          }

          // Calculate if there are more records
          const totalFetched = actualOffset + formattedRequests.length;
          setHasMore(
            count ? totalFetched < count : formattedRequests.length === pageSize
          );

          if (loadMore) {
            pageRef.current = currentPage + 1;
          } else {
            pageRef.current = 0;
          }

          return formattedRequests;
        }
      } catch (error) {
        console.error("Error fetching por_facturar sales notes:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        if (!loadMore) setHasLoaded(true);
      }
    },
    [currentUserId, userRole, viewAllSellers]
  );

  const loadMorePorFacturarNotes = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchPorFacturarNotes(true);
    }
  }, [fetchPorFacturarNotes, isLoadingMore, hasMore]);

  const isRefreshingRef = useRef(false);

  // Silently refresh the currently-loaded window when a sales request changes
  // status, so cards enter/leave this column live (no page reload needed).
  const refreshPorFacturarNotes = useCallback(async () => {
    if (!isInitialized.current || isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      const windowSize =
        POR_FACTURAR_INITIAL_SIZE +
        pageRef.current * POR_FACTURAR_LOAD_MORE_SIZE;

      let query = supabase
        .from("sales_request")
        .select(
          `
          *,
          paid_amount,
          clients:client_id(id, name, risk_checked),
          profiles:created_by(id, name, lastname, email, role)
        `,
          { count: "exact" }
        )
        .eq("status", "por_facturar")
        .is("deleted_at", null)
        .range(0, windowSize - 1);

      if (userRole === "seller" && currentUserId && !viewAllSellers) {
        query = query.eq("created_by", currentUserId);
      } else if (userRole === "transportista") {
        query = query.eq("assigned_transportista", currentUserId);
      }

      const { data, error, count } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;
      if (!data) return;

      setPorFacturarNotes(data.map(mapRow));
      setPorFacturarClientNames(extractClientNames(data));
      setHasMore(count ? data.length < count : data.length === windowSize);
    } catch (error) {
      console.error("Error refreshing por_facturar sales notes:", error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [currentUserId, userRole, viewAllSellers]);

  useSalesRequestRealtimeRefresh(refreshPorFacturarNotes);

  return {
    porFacturarNotes,
    porFacturarClientNames,
    isLoading,
    isLoadingMore,
    hasMore,
    hasLoaded,
    fetchPorFacturarNotes,
    loadMorePorFacturarNotes,
  };
};
